/*
 * Gingerglow: automatic discount tiers (GGL5 / GGL7 / GGL10) in the cart drawer.
 *
 * The cart page applies the tier code with its own script (sections/main-cart.liquid). Customers who check out
 * from the drawer never open that page, so this does the same work there:
 *   1. After every drawer refresh (Gecko fires "cart:updated"), read the state rendered by
 *      snippets/gingerflow-auto-discount.liquid (part 'state') and add / swap / remove the GGL code in the cart,
 *      keeping any code the customer entered. Then ask Gecko to redraw the drawer ("cart:refresh").
 *      The tier bar at the top of the drawer is refreshed from a template at the same time.
 *   2. On Check Out, fill the drawer's hidden "discount" field with the codes to keep. Gecko leaves it empty,
 *      and an empty field submitted with the checkout button drops the codes already in the cart.
 *
 * Loaded from layout/theme.liquid on every page except the cart page (no drawer there).
 */
(function () {
  'use strict';
  if (window.gingerflowDrawerDiscount) return;
  window.gingerflowDrawerDiscount = true;

  var DRAWER = '#t4s-mini_cart';
  var STATE = '[data-gingerflow-auto-discount-state]';
  // Same key and lifetime as the cart page, so the two never retry the same failed swap back to back.
  var ATTEMPT_KEY = 'gingerflow:auto-discount-attempt';
  var ATTEMPT_TTL = 5000;
  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var UPDATE_URL = ROOT + 'cart/update.js';

  var unique = function (list) {
    var seen = {};
    return list.filter(function (code) {
      var key = code.toUpperCase();
      if (!code || seen[key]) return false;
      seen[key] = true;
      return true;
    });
  };
  var splitCodes = function (value) {
    return unique(String(value || '').split(',').map(function (code) { return code.trim(); }));
  };

  var stateEl = function () {
    var drawer = document.querySelector(DRAWER);
    return drawer ? drawer.querySelector(STATE) : null;
  };

  // The tier bar sits outside the parts Gecko redraws; refresh it from the template Gecko just redrew,
  // letting the fill slide from its old width to the new one.
  var refreshBar = function () {
    var drawer = document.querySelector(DRAWER);
    if (!drawer) return;
    var slot = drawer.querySelector('[data-gingerflow-drawer-tiers]');
    var next = drawer.querySelector('[data-gingerflow-drawer-tiers-next]');
    if (!slot || !next) return;
    var html = next.innerHTML.trim();
    if (slot.innerHTML.trim() === html) return;
    var oldFill = slot.querySelector('.gingerflow-drawer-tiers__fill');
    var from = oldFill ? oldFill.style.width : '';
    slot.innerHTML = html;
    var fill = slot.querySelector('.gingerflow-drawer-tiers__fill');
    if (fill && from && from !== fill.style.width) {
      var to = fill.style.width;
      fill.style.width = from;
      void fill.offsetWidth;
      fill.style.width = to;
    }
  };

  var tiersOf = function (el) {
    var minimums = (el.dataset.minimums || '').split(',');
    var codes = (el.dataset.codes || '').split(',');
    return minimums.map(function (minimum, index) {
      return { minimum: Number(minimum), code: (codes[index] || '').trim().toUpperCase() };
    }).filter(function (tier) {
      return tier.code && isFinite(tier.minimum);
    }).sort(function (a, b) { return b.minimum - a.minimum; });
  };

  // What the cart's code list should become, or null when the right GGL code (or none) is already applied.
  var planFor = function (el) {
    var tiers = tiersOf(el);
    if (!tiers.length) return null;
    var subtotal = Number(el.dataset.subtotal) || 0;
    var targetTier = tiers.find(function (tier) { return subtotal >= tier.minimum; });
    var target = targetTier ? targetTier.code : null;
    var autoCodes = tiers.map(function (tier) { return tier.code; });
    var existing = splitCodes(el.dataset.applied);
    var normalized = existing.map(function (code) { return code.toUpperCase(); });
    var currentAuto = normalized.filter(function (code) { return autoCodes.indexOf(code) > -1; });
    if (currentAuto.length === (target ? 1 : 0) && (!target || currentAuto[0] === target)) return null;
    return {
      target: target,
      autoCodes: autoCodes,
      currentAuto: currentAuto,
      preserved: existing.filter(function (code, index) { return autoCodes.indexOf(normalized[index]) === -1; }),
      signature: subtotal + ':' + (target || 'none') + ':' + normalized.slice().sort().join(',')
    };
  };

  var hasRecentAttempt = function (signature) {
    try {
      var attempt = JSON.parse(sessionStorage.getItem(ATTEMPT_KEY));
      return !!attempt && attempt.signature === signature && Date.now() - attempt.timestamp < ATTEMPT_TTL;
    } catch (error) {
      return false;
    }
  };
  var rememberAttempt = function (signature) {
    try { sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify({ signature: signature, timestamp: Date.now() })); } catch (error) {}
  };
  var clearAttempt = function () {
    try { sessionStorage.removeItem(ATTEMPT_KEY); } catch (error) {}
  };

  // Dim the drawer totals while the code is being swapped; cleared on the next redraw (or after a few seconds).
  var busyTimer = null;
  var setBusy = function (on) {
    var drawer = document.querySelector(DRAWER);
    if (drawer) drawer.classList.toggle('is--gingerflow-busy', on);
    clearTimeout(busyTimer);
    if (on) busyTimer = setTimeout(function () { setBusy(false); }, 6000);
  };

  // Replaces the cart's whole discount-code list.
  var updateCodes = async function (codes) {
    var response = await fetch(UPDATE_URL, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ discount: codes.join(',') })
    });
    if (!response.ok) throw new Error('gingerflow drawer discount: ' + response.status);
    return response.json();
  };
  var findCode = function (data, code) {
    var wanted = code.toUpperCase();
    return (data.discount_codes || []).find(function (discount) {
      return discount.code && discount.code.toUpperCase() === wanted;
    });
  };

  var syncing = false;
  var queued = false;
  var failedSignature = null;
  // Hard stop per page view: never swap codes more than twice for the same cart state.
  var runs = {};

  var sync = async function (plan) {
    if (syncing) {
      queued = true;
      return;
    }
    if (hasRecentAttempt(plan.signature) || (runs[plan.signature] || 0) >= 2) {
      setBusy(false);
      return;
    }
    runs[plan.signature] = (runs[plan.signature] || 0) + 1;
    rememberAttempt(plan.signature);
    syncing = true;
    setBusy(true);
    var changed = false;
    try {
      var target = plan.target;
      var desired = target ? plan.preserved.concat(target) : plan.preserved;
      var data = await updateCodes(desired);
      changed = true;

      // Shopify can keep the previous GGL code when the list is replaced in one request;
      // only then clear the managed codes first and add the target again.
      var stale = (data.discount_codes || []).some(function (discount) {
        var code = (discount.code || '').toUpperCase();
        return plan.autoCodes.indexOf(code) > -1 && code !== target;
      });
      if (stale) {
        data = await updateCodes(plan.preserved);
        if (target) data = await updateCodes(desired);
      }

      if (target) {
        var applied = findCode(data, target);
        // Never let the automatic code knock out a code the customer entered.
        var knockedOut = plan.preserved.some(function (code) {
          var after = findCode(data, code);
          return !after || after.applicable === false;
        });
        if (!applied || applied.applicable === false || knockedOut) {
          await updateCodes(plan.currentAuto[0] ? plan.preserved.concat(plan.currentAuto[0]) : plan.preserved);
          failedSignature = plan.signature;
          rememberAttempt(plan.signature);
        } else {
          failedSignature = null;
          clearAttempt();
        }
      } else {
        failedSignature = null;
        clearAttempt();
      }
    } finally {
      syncing = false;
      if (changed) {
        // Gecko refetches the drawer, redraws it and fires cart:updated again (then there is nothing left to do).
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      } else {
        setBusy(false);
      }
      if (queued) {
        queued = false;
        setTimeout(onCartChanged, 0);
      }
    }
  };

  var onCartChanged = function () {
    refreshBar();
    var el = stateEl();
    var plan = el ? planFor(el) : null;
    if (!plan) {
      setBusy(false);
      return;
    }
    sync(plan).catch(function (error) {
      clearAttempt();
      setBusy(false);
      console.error(error);
    });
  };

  document.addEventListener('cart:updated', onCartChanged);
  // The drawer is fetched after page load; if it is already there, check it now.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onCartChanged);
  else onCartChanged();

  // Check Out from the drawer: send the codes to keep instead of Gecko's empty / stale "discount" field.
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.closest || !form.closest(DRAWER)) return;
    var input = form.querySelector('[data-cart-discount]');
    if (!input) return;
    var el = stateEl();
    var autoCodes = el ? tiersOf(el).map(function (tier) { return tier.code; }) : [];
    var isAuto = function (code) { return autoCodes.indexOf(code.toUpperCase()) > -1; };
    var applied = el ? splitCodes(el.dataset.applied) : [];
    var codes = applied.filter(function (code) { return !isAuto(code); })
      // A code the customer saved in the drawer's coupon tool (Gecko keeps it in this field).
      .concat(splitCodes(input.value).filter(function (code) { return !isAuto(code); }));
    var plan = el ? planFor(el) : null;
    var autoCode = plan
      ? (plan.signature === failedSignature ? plan.currentAuto[0] : plan.target)
      : applied.filter(isAuto)[0];
    if (autoCode) codes.push(autoCode);
    codes = unique(codes);
    if (codes.length) {
      input.setAttribute('name', 'discount');
      input.value = codes.join(',');
    } else {
      input.removeAttribute('name');
      input.value = '';
    }
  }, true);
})();
